import type { DataPoints } from '@retracedgmbh/data-points-lib';
import DataPointsLib from '@retracedgmbh/data-points-lib';
import {
  actorFromAuditMeta,
  ENTITY_HISTORY_ENTITY_TYPES,
  ENTITY_HISTORY_EVENT_TYPES,
  ENTITY_HISTORY_SOURCES,
  EntityHistoryService,
  type EntityHistorySource,
} from '../../../../entity-history/index.js';
import type { DbClient } from '../../../../lib/db.js';
import {
  getNotificationService,
  type IJobQueue,
  type NotificationService,
} from '../../../../lib/notifications/index.js';
import {
  COMPANY_CORE_INFO_TEMPLATES,
  COMPANY_CORE_INFO_TEMPLATES_ENUMS,
  CONNECTION_TYPES_PAIR,
  RELATIONAL_DATA_POINTS_TO_COPY,
} from '../../../constants.js';
import { saveCompanyDataPointsAndTemplateAnswersDb } from '../../db/companies/save-data-points-and-template-answers.js';
import {
  type CompanyProfileRow,
  getCompanyAboutProfileDb,
} from '../../db/company-profiles/get-about-profile.js';
import { getCompanyProfileWithMetaByIdsDb } from '../../db/company-profiles/get-with-meta-by-ids.js';
import { getDataPointAnswersByDataPointsDb } from '../../db/data-point-answers/get-by-data-points.js';
import type { DataPointAnswerRow } from '../../db/data-point-answers/types.js';
import { listTemplatesUnscopedDb } from '../../db/templates/list-unscoped.js';
import type { Template } from '../../db/templates/types.js';
import { assertCompanyExists } from '../guards.js';
import type { CompanyIds } from '../types.js';

const ABOUT_TEMPLATE_ID = COMPANY_CORE_INFO_TEMPLATES.ABOUT;
const OTHER_IDS_TEMPLATE_ID = COMPANY_CORE_INFO_TEMPLATES.OTHER_IDS;

type DataPointValue = { value: unknown };
type TemplateAnswer = Record<string, DataPointValue>;
type TemplateAnswers = Record<string, TemplateAnswer>;
type AuditData = Record<string, unknown>;

/** What the profile history needs to know about the request doing the link. */
type LinkHistoryContext = {
  /** Where the link came from, for the profile history; defaults to WEB. */
  source?: EntityHistorySource;
  correlationId?: string | null;
};

/**
 * The `company-linked` notification: tells the linked company's users who
 * linked to them. The backend's `company-linked` handler consumes it.
 */
export type CompanyLinkedNotification = {
  type: 'company-linked';
  payload: {
    /** The company that made the link. */
    linkingCompanyId: string;
    /** The company it linked to — whose users are notified. */
    linkedCompanyId: string;
    /** What the linking company now calls the linked one. */
    connectionType: string;
  };
};

export type CompanyLinkServiceDeps = {
  db: DbClient;
  queue: IJobQueue;
};

export type CompanyLinkSingleParams = {
  forCompanyId: string;
  byCompanyId: string;
  connectionType: string;
  auditData: AuditData;
  /**
   * Legacy `handler.js` only published the company-linked event when
   * `byCompanyId === ctx.companyId` — true for every call from the `/link`
   * route (byCompanyId is always the caller's own company there), but NOT
   * true for the manual "link the other direction too" calls made by
   * `api-service`'s `supplychains.js`/`supplier-network.js`, which pass an
   * arbitrary `byCompanyId`. Those callers must pass `notifyLink: false` for
   * the direction that doesn't match their own `ctx.companyId`. Defaults to
   * `true` since every other caller satisfies the invariant.
   */
  notifyLink?: boolean;
} & LinkHistoryContext;

export type CompanyLinkBulkParams = {
  forCompanyIds: string[];
  byCompanyId: string;
  connectionType: string;
  auditData: AuditData;
} & LinkHistoryContext;

/**
 * Ports the legacy `POST companies-service/v1/companies/link` write path:
 * links two companies bidirectionally (a forward profile using the request's
 * `connectionType`, plus a reverse profile using its pair) and notifies the
 * linked company.
 */
export class CompanyLinkService {
  private readonly dataPoints: DataPoints;
  private readonly history: EntityHistoryService;
  private readonly notifications: NotificationService;

  constructor(private readonly deps: CompanyLinkServiceDeps) {
    this.dataPoints = new DataPointsLib(console).initializeDataPoints();
    this.history = new EntityHistoryService({ db: deps.db });
    this.notifications = getNotificationService(deps);
  }

  async linkSingle(params: CompanyLinkSingleParams): Promise<void> {
    const {
      forCompanyId,
      byCompanyId,
      connectionType,
      auditData,
      notifyLink = true,
      source,
      correlationId,
    } = params;
    const history: LinkHistoryContext = { source, correlationId };

    await assertCompanyExists(this.deps.db, forCompanyId, {
      message: 'Company does not exist',
      details: { forCompanyId },
    });

    const companyIds: CompanyIds = {
      forCompanyId,
      byCompanyId,
      onBehalfOfCompanyId: byCompanyId,
    };

    await this.performLink(companyIds, connectionType, auditData, history);
    await this.linkReverse(companyIds, connectionType, auditData, history);

    // Self-link: the DB writes above still happen, but there's no
    // notification tail — matches legacy's early `return`.
    if (forCompanyId === byCompanyId) return;

    if (notifyLink) {
      await this.notifyCompanyLinked(companyIds, connectionType);
    }

    // Linking used to also copy byCompanyId's public audits onto forCompanyId
    // as READ grants. It no longer does: a public audit is visible to everyone
    // the audited company is connected to, decided when the list is read
    // (`buildPublicToConnectedSql` in `modules/audits`). Connecting therefore
    // grants the network's public audits by itself, and — unlike the copy —
    // does so however the link was written [DELTA-2545].
  }

  async linkBulk(params: CompanyLinkBulkParams): Promise<void> {
    const {
      forCompanyIds,
      byCompanyId,
      connectionType,
      auditData,
      source,
      correlationId,
    } = params;
    const history: LinkHistoryContext = { source, correlationId };

    for (const forCompanyId of forCompanyIds) {
      // Unlike linkSingle, bulk skips a self-link id entirely — no DB
      // writes — matching legacy `bulkLink`.
      if (forCompanyId === byCompanyId) continue;

      await assertCompanyExists(this.deps.db, forCompanyId, {
        message: 'Company does not exist',
        details: { forCompanyId },
      });

      const companyIds: CompanyIds = {
        forCompanyId,
        byCompanyId,
        onBehalfOfCompanyId: byCompanyId,
      };

      const profile = await this.performLink(
        companyIds,
        connectionType,
        auditData,
        history,
      );
      await this.linkReverse(companyIds, connectionType, auditData, history);

      if (profile) {
        await this.notifyCompanyLinked(companyIds, connectionType);
      }
    }
  }

  /**
   * Queues the `company-linked` notification for the linked company. The link
   * is already written, so a queue failure is only logged.
   */
  private async notifyCompanyLinked(
    { forCompanyId, byCompanyId }: CompanyIds,
    connectionType: string,
  ): Promise<void> {
    const notification: CompanyLinkedNotification = {
      type: 'company-linked',
      payload: {
        linkingCompanyId: byCompanyId,
        linkedCompanyId: forCompanyId,
        connectionType,
      },
    };

    await this.notifications.send(notification).catch(error => {
      console.error(
        { error, notification },
        'Failed to queue company linked notification',
      );
    });
  }

  /** Forward write: builds & persists companyIds' network profile of forCompanyId. */
  private async performLink(
    companyIds: CompanyIds,
    connectionType: string,
    auditData: AuditData,
    history: LinkHistoryContext,
  ): Promise<CompanyProfileRow | undefined> {
    const templateAnswers = await this.buildTemplateAnswers(companyIds);
    if (templateAnswers[ABOUT_TEMPLATE_ID]) {
      templateAnswers[ABOUT_TEMPLATE_ID].connection_type = {
        value: connectionType,
      };
    }

    await saveCompanyDataPointsAndTemplateAnswersDb(this.deps.db, {
      companyIds,
      templateAnswers,
      auditData,
    });
    await this.recordLinkHistory(
      companyIds,
      connectionType,
      auditData,
      history,
    );

    return getCompanyAboutProfileDb(this.deps.db, companyIds);
  }

  /** The `CREATED` entry that opens a network profile's history. */
  private async recordLinkHistory(
    companyIds: CompanyIds,
    connectionType: string,
    auditData: AuditData,
    history: LinkHistoryContext,
  ): Promise<void> {
    await this.history.record({
      entityType: ENTITY_HISTORY_ENTITY_TYPES.COMPANY_PROFILE,
      entityId: companyIds.forCompanyId,
      entitySubType: ABOUT_TEMPLATE_ID,
      eventType: ENTITY_HISTORY_EVENT_TYPES.CREATED,
      source: history.source ?? ENTITY_HISTORY_SOURCES.WEB,
      correlationId: history.correlationId,
      companyIds,
      changes: null,
      metadata: {
        entityType: ENTITY_HISTORY_ENTITY_TYPES.COMPANY_PROFILE,
        templateId: ABOUT_TEMPLATE_ID,
        connectionType,
      },
      createdBy: actorFromAuditMeta(auditData),
    });
  }

  /**
   * Reverse write: the other company's profile of the caller, using the
   * paired connectionType. Deliberately has NO existence check — unlike
   * `CompanyCreationService.linkOtherId`, this always rebuilds the reverse
   * profile, because the pair passed in is never null. Never throws — a
   * reverse-link failure must not fail the primary link request.
   */
  private async linkReverse(
    companyIds: CompanyIds,
    connectionType: string,
    auditData: AuditData,
    history: LinkHistoryContext,
  ): Promise<void> {
    try {
      if (companyIds.forCompanyId === companyIds.byCompanyId) return;

      const reverseIds: CompanyIds = {
        forCompanyId: companyIds.byCompanyId,
        byCompanyId: companyIds.forCompanyId,
        onBehalfOfCompanyId: companyIds.forCompanyId,
      };
      const pair = CONNECTION_TYPES_PAIR[connectionType];

      const templateAnswers = await this.buildTemplateAnswers(reverseIds);
      if (templateAnswers[ABOUT_TEMPLATE_ID]) {
        templateAnswers[ABOUT_TEMPLATE_ID].connection_type = { value: pair };
      }

      await saveCompanyDataPointsAndTemplateAnswersDb(this.deps.db, {
        companyIds: reverseIds,
        templateAnswers,
        auditData,
      });
      await this.recordLinkHistory(reverseIds, pair, auditData, history);
    } catch (error) {
      // Non-propagating — matches legacy `linkOtherId` — but logged: a failure
      // part-way leaves the other company with an incomplete view of the
      // caller (e.g. ABOUT alone, no link row) and the request still succeeds.
      console.error(
        { error, companyIds, connectionType },
        'Failed to write the reverse direction of a company link',
      );
    }
  }

  /**
   * Faithful port of legacy `buildTemplatesFromDataPointAnswers`: preserves
   * any answers already stored for the exact companyIds triple across every
   * template, then backfills/merges only the 3 core templates from
   * `forCompanyId`'s own defaults. On a brand-new pair (the common case) this
   * degenerates to "copy forCompanyId's core-template defaults" — but on a
   * re-link of an already-linked pair, previously-customized answers on
   * non-core templates are preserved rather than silently overwritten.
   */
  private async buildTemplateAnswers(
    companyIds: CompanyIds,
  ): Promise<TemplateAnswers> {
    const templates = await listTemplatesUnscopedDb(this.deps.db);
    const dataPointIds = templates.flatMap(t => t.composedOf);

    const existingAnswers = await getDataPointAnswersByDataPointsDb(
      this.deps.db,
      {
        ...companyIds,
        retracedDataPointIds: dataPointIds,
      },
    );

    const constructed = this.buildDataPointsAnswers(
      dataPointIds,
      existingAnswers,
    );

    // A data point can be correctly set on an existing profile row's blob
    // without a matching answer row backing it (e.g. a profile written by a
    // path that sets the blob directly, or one of this platform's own
    // reconciliation migrations) -- `constructed` above would otherwise show
    // it as {value: null} and silently wipe it, since
    // saveCompanyDataPointsAndTemplateAnswersDb fully replaces the profile on
    // write. Same fallback as buildCompanyProfileForPatch
    // (background-jobs/jobs/companies/global-data-point-sync.ts) and
    // PatchTemplateAnswersService. Enriching `constructed` here (rather than
    // patching templateAnswers after pickTemplateComposedOf) means its
    // existing all-null exclusion still applies correctly to templates that
    // remain genuinely empty. Sequential -- never parallelise DB queries.
    for (const template of templates) {
      const existingProfile = await getCompanyProfileWithMetaByIdsDb(
        this.deps.db,
        { ...companyIds, profileType: template.id },
      );
      if (!existingProfile) continue;
      const existingData = (existingProfile.data ?? {}) as Record<
        string,
        unknown
      >;
      for (const dataPointId of template.composedOf) {
        if (constructed[dataPointId]?.value != null) continue;
        const raw = existingData[dataPointId] as
          | Record<string, unknown>
          | null
          | undefined;
        if (raw?.value == null) continue;
        constructed[dataPointId] = raw as DataPointValue;
      }
    }

    const templateAnswers = this.pickTemplateComposedOf(constructed, templates);

    for (const coreTemplateId of COMPANY_CORE_INFO_TEMPLATES_ENUMS) {
      const current = templateAnswers[coreTemplateId];
      const template = templates.find(t => t.id === coreTemplateId);
      const defaults = await this.buildDefaultTemplateAnswers(
        companyIds.forCompanyId,
        template,
      );
      if (!defaults) continue;

      templateAnswers[coreTemplateId] = current
        ? this.mergeTemplateAnswers(current, defaults)
        : defaults;
    }

    // retraced_id is a SYSTEM data point, so buildDefaultTemplateAnswers'
    // GLOBAL/RELATIONAL filter always excludes it from the copied defaults
    // above -- it must be set directly to the profiled company's own id
    // rather than copied, the same as withRetracedId() in
    // CompanyCreationService.
    if (templateAnswers[OTHER_IDS_TEMPLATE_ID]) {
      templateAnswers[OTHER_IDS_TEMPLATE_ID].retraced_id = {
        value: companyIds.forCompanyId,
      };
    }

    return templateAnswers;
  }

  private buildDataPointsAnswers(
    dataPointIds: string[],
    answers: DataPointAnswerRow[],
  ): TemplateAnswer {
    const result: TemplateAnswer = {};
    for (const id of dataPointIds) {
      result[id] = { value: null };

      const row = answers.find(a => a.retracedDataPointId === id);
      if (!row?.answer) continue;

      const parsed = (
        typeof row.answer === 'string' ? JSON.parse(row.answer) : row.answer
      ) as DataPointValue;
      if (parsed?.value == null) continue;

      result[id] = parsed;
    }
    return result;
  }

  private pickTemplateComposedOf(
    constructed: TemplateAnswer,
    templates: Template[],
  ): TemplateAnswers {
    const templateAnswers: TemplateAnswers = {};
    for (const template of templates) {
      const picked: TemplateAnswer = {};
      for (const id of template.composedOf) {
        picked[id] = constructed[id];
      }
      const allNull = Object.values(picked).every(v => v?.value == null);
      if (allNull) continue;

      templateAnswers[template.id] = picked;
    }
    return templateAnswers;
  }

  /**
   * `main` and `secondary` are always built from the same template's
   * `composedOf`, so their key sets are identical — no need to guard a
   * missing key on either side.
   */
  private mergeTemplateAnswers(
    main: TemplateAnswer,
    secondary: TemplateAnswer,
  ): TemplateAnswer {
    const merged: TemplateAnswer = {};
    for (const key of Object.keys(main)) {
      merged[key] = main[key].value !== null ? main[key] : secondary[key];
    }
    return merged;
  }

  private async buildDefaultTemplateAnswers(
    companyId: string,
    template: Template | undefined,
  ): Promise<TemplateAnswer | null> {
    if (!template) return null;

    const rows = await getDataPointAnswersByDataPointsDb(this.deps.db, {
      forCompanyId: companyId,
      byCompanyId: companyId,
      onBehalfOfCompanyId: companyId,
      retracedDataPointIds: template.composedOf,
    });

    const filtered = rows.filter(row => {
      let dp: ReturnType<DataPoints['getDataPointById']> | undefined;
      try {
        dp = this.dataPoints.getDataPointById(row.retracedDataPointId);
      } catch {
        return false;
      }
      const type = dp?.dataPointDefinition?.type;
      return (
        type === 'GLOBAL' ||
        (type === 'RELATIONAL' &&
          RELATIONAL_DATA_POINTS_TO_COPY.includes(row.retracedDataPointId))
      );
    });

    return this.buildDataPointsAnswers(template.composedOf, filtered);
  }
}
